import { cx } from '@linaria/core';
import { useRef, useState, useCallback } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { pm2ConfigStore } from '@pm2/store';
import { usePm2Modal } from '@pm2/ui-lightbox';
import { Button } from '@ui/kit';
import { ExternalLinkIcon } from '@ui/kit/icons';
import { CSSRefTransition } from '@utils/react';
import { PostloadedIframe } from './atom.postloaded-iframe.js';
import { Preview } from './atom.preview.js';
import { Boundary } from './comp.boundary.js';
import { externalLinkButton, iframeWrapperCls, fadeInCls, FADE_TRANSITION_DURATION_MS, errorMessageCls, errorMessageButtonCls, contentCls } from './comp.content.style.js';
import { useShouldRender } from './hook.should-render.js';
import { shouldOpenInLightbox } from './util.should-open-in-lightbox.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

function IframeComponentContent(props) {
  const modal = usePm2Modal();
  const audience = pm2ConfigStore.useSelector(state => state.config.theme.targetAudience);
  const loginDisabled = pm2ConfigStore.useSelector(state => state.config.settings.loginDisabled);
  const ref = useRef(null);
  const forceOpenInLightbox = !props.autoActivate;
  const [hasInteracted, setHasInteracted] = useState(false);
  const [resolvedPreviewImage, setResolvedPreviewImage] = useState();
  const shouldRenderIframe = useShouldRender({
    disabled: loginDisabled,
    componentRef: ref,
    displayData: props.displayData,
    learningObjectId: props.learningObjectId,
    activatedByInteraction: hasInteracted,
    forceOpenInLightbox,
    autoActivate: props.autoActivate
  });
  const openInLightbox = useCallback(() => {
    if (props.displayData && props.target === "_blank") {
      const forwardTo = window.gProxy.forwardTo(props.displayData.src);
      window.open(forwardTo, "_blank");
    } else if (props.displayData) {
      modal.fromIframe(/*#__PURE__*/jsx(Boundary, {
        hasPreviewImage: false,
        previewImageDimensions: resolvedPreviewImage,
        forceOpenInLightbox: false,
        displayData: props.displayData,
        inLightbox: true,
        children: /*#__PURE__*/jsx(PostloadedIframe, {
          src: props.displayData.src,
          title: props.title
        })
      }));
    }
  }, [props.displayData, props.target, props.title, modal, resolvedPreviewImage]);
  const onPreviewClick = useCallback(() => {
    if (loginDisabled) {
      return;
    }
    setHasInteracted(true);

    // In case the iframe should be opened in a lightbox and not in embed-mode,
    // then trigger that immediately on preview click.
    if (shouldOpenInLightbox({
      displayData: props.displayData,
      componentRef: ref,
      forceOpenInLightbox
    })) {
      openInLightbox();
    }
  }, [loginDisabled, props.displayData, forceOpenInLightbox, openInLightbox]);
  const handlePreviewImageLoaded = useCallback(img => {
    setResolvedPreviewImage({
      scale: img.naturalHeight / img.naturalWidth,
      maxWidth: img.naturalWidth,
      maxHeight: img.naturalHeight,
      src: img.src
    });
  }, []);
  return /*#__PURE__*/jsxs(Fragment, {
    children: [props.contentType === "i-book" && /*#__PURE__*/jsx(Button, {
      className: externalLinkButton,
      as: "a",
      icon: /*#__PURE__*/jsx(ExternalLinkIcon, {
        height: 20
      }),
      theme: "primary",
      href: props.displayData?.src,
      target: "_blank",
      "data-ibook-button": true,
      children: "\xC5bn i-bog i ny fane"
    }), /*#__PURE__*/jsx(Boundary, {
      hasPreviewImage: false,
      previewImageDimensions: resolvedPreviewImage,
      forceOpenInLightbox: forceOpenInLightbox,
      displayData: props.displayData,
      children: /*#__PURE__*/jsxs("div", {
        ref: ref,
        className: cx(contentCls, audience === "Grade1To3" && "simple", props.contentType === "i-book" && "withExternalLink"),
        children: [!shouldRenderIframe && /*#__PURE__*/jsx(Preview, {
          title: props.title,
          previewImageSrc: resolvedPreviewImage?.src,
          isRenderingIframe: shouldRenderIframe,
          displayData: props.displayData,
          forceOpenInLightbox: props.forceOpenInLightbox,
          onClick: onPreviewClick,
          onPreviewImageLoaded: handlePreviewImageLoaded
        }), shouldRenderIframe && /*#__PURE__*/jsx("div", {
          className: iframeWrapperCls,
          children: /*#__PURE__*/jsxs(TransitionGroup, {
            component: null,
            appear: true,
            children: [props.displayData && /*#__PURE__*/jsx(CSSRefTransition, {
              timeout: FADE_TRANSITION_DURATION_MS,
              classNames: fadeInCls,
              children: ref => /*#__PURE__*/jsx(PostloadedIframe, {
                ref: ref,
                src: props.displayData?.src,
                title: props.title
              })
            }, "iframe"), props.failedFetchingDisplayData && /*#__PURE__*/jsx(CSSRefTransition, {
              timeout: FADE_TRANSITION_DURATION_MS,
              classNames: fadeInCls,
              children: ref => /*#__PURE__*/jsxs("div", {
                ref: ref,
                className: errorMessageCls,
                children: [/*#__PURE__*/jsx("span", {
                  children: "Noget gik galt da vi fors\xF8gte at indl\xE6se komponenten."
                }), /*#__PURE__*/jsx("button", {
                  onClick: props.retryFetchingDisplayData,
                  className: errorMessageButtonCls,
                  children: "Klik her for at pr\xF8ve igen"
                })]
              })
            }, "error")]
          })
        })]
      })
    })]
  });
}

export { IframeComponentContent };
//# sourceMappingURL=comp.content.js.map
